import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Copy, ShieldCheck, Zap, HeartHandshake, Info } from "lucide-react";

const FLOW_LABELS = {
  invoice:         "Ward Fees & Invoices (parent portal)",
  membership:      "Membership Purchase (public + member portal)",
  wallet_topup:    "Ward Wallet Top-up",
  tournament_team: "Tournament Team Registration",
  donation:        "80G Donations",
};

// Two-provider config screen. Razorpay stays intact during PhonePe Phase 1
// migration so production flows never break; PhonePe is set up in parallel
// and switched over once tested end-to-end.
export default function AdminPaymentConfig() {
  const [tab, setTab] = useState("phonepe");

  return (
    <div data-testid="admin-payment-config">
      <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-3">// System · Payment Gateway</div>
      <h1 className="font-display text-4xl font-black uppercase tracking-tighter leading-none mb-6">
        Payment Gateway.
      </h1>

      {/* Migration banner */}
      <div className="max-w-3xl border border-brand-orange/40 bg-brand-orange/10 p-4 mb-6 flex items-start gap-3" data-testid="pc-migration-banner">
        <Info className="brand-orange shrink-0 mt-0.5" size={18} />
        <div className="text-xs text-white/70 leading-relaxed">
          <b className="text-white">Migration in progress:</b> We are switching payments from
          Razorpay to <b className="brand-orange">PhonePe (UPI only)</b>. Configure PhonePe below,
          run <b>Test Connection</b>, and we&apos;ll swap the frontend checkouts one flow at a time.
          Razorpay stays live until the swap is complete.
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-white/10">
        {[
          { id: "phonepe", label: "PhonePe (UPI)", badge: "NEW" },
          { id: "razorpay", label: "Razorpay", badge: "LEGACY" },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`pc-tab-${t.id}`}
            className={`px-5 py-3 font-mono text-xs uppercase tracking-widest border-b-2 -mb-px transition-colors ${
              tab === t.id ? "border-brand-orange brand-orange" : "border-transparent text-white/50 hover:text-white/80"
            }`}
          >
            {t.label}
            <span className="ml-2 text-[9px] text-white/40">{t.badge}</span>
          </button>
        ))}
      </div>

      {tab === "phonepe" ? <PhonePePanel /> : <RazorpayPanel />}
    </div>
  );
}

// ---------------- PhonePe ----------------
function PhonePePanel() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({
    env: "sandbox",
    merchant_id: "",
    client_id: "",
    client_secret: "",
    client_version: 1,
    webhook_username: "",
    webhook_password: "",
    webhook_hmac_key_id: "",
    webhook_hmac_secret: "",
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [webhookUrl, setWebhookUrl] = useState("");

  useEffect(() => {
    load();
    setWebhookUrl(`${window.location.origin}/api/payments/phonepe/webhook`);
  }, []);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/phonepe/config");
      setCfg(data);
      setForm((f) => ({
        ...f,
        env: data.env || "sandbox",
        merchant_id: data.merchant_id || "",
        client_id: data.client_id || "",
        client_version: data.client_version || 1,
        webhook_username: data.webhook_username || "",
        webhook_hmac_key_id: data.webhook_hmac_key_id || "",
        client_secret: "",
        webhook_password: "",
        webhook_hmac_secret: "",
      }));
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggleFlow = async (kind, enabled) => {
    try {
      const { data } = await api.post("/admin/phonepe/enable-flow", { kind, enabled });
      setCfg((c) => ({ ...c, enabled_flows: data.enabled_flows }));
      toast.success(enabled ? `${kind} → PhonePe live` : `${kind} → Razorpay (rolled back)`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/admin/phonepe/config", form);
      toast.success("PhonePe credentials saved");
      // Clear secret inputs, reload masked view
      setForm((f) => ({ ...f, client_secret: "", webhook_password: "", webhook_hmac_secret: "" }));
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const testConnection = async () => {
    setTesting(true); setTestResult(null);
    try {
      const { data } = await api.post("/admin/phonepe/test-connection");
      setTestResult(data);
      if (data.ok) toast.success("PhonePe credentials verified");
      else toast.error(data.message || "Test failed");
    } catch (e) { toast.error(apiError(e)); setTestResult({ ok: false, message: apiError(e) }); }
    finally { setTesting(false); }
  };

  const copy = (v) => { navigator.clipboard.writeText(v); toast.success("Copied"); };

  if (!cfg) return <div className="text-white/60">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="phonepe-panel">
      {/* Status band */}
      <div className={`border p-6 ${cfg.is_configured ? "border-green-500/50 bg-green-500/10" : "border-white/12 bg-[#131A2B]"}`}>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className={`h-3 w-3 rounded-full ${cfg.is_configured ? "bg-green-400 animate-pulse" : "bg-white/30"}`} />
            <div>
              <div className="font-display text-2xl uppercase font-black leading-none">
                {cfg.is_configured ? "PHONEPE CONFIGURED" : "PHONEPE NOT SET UP"}
              </div>
              <div className="font-mono text-xs uppercase tracking-widest text-white/50 mt-1">
                Env · <span className={cfg.env === "production" ? "text-brand-orange" : "brand-blue"}>{(cfg.env || "sandbox").toUpperCase()}</span>
                {cfg.configured_at ? <> · <span className="text-white/40">saved {new Date(cfg.configured_at).toLocaleString()}</span></> : null}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 text-white/60 text-xs">
            <ShieldCheck size={16} className="brand-orange" />
            UPI ONLY · INTENT + COLLECT
          </div>
        </div>
      </div>

      {/* Config form */}
      <div className="max-w-3xl bg-[#131A2B] border border-white/12 p-8 space-y-6" data-testid="pp-form">
        <div>
          <div className="font-display text-lg uppercase font-bold brand-orange mb-1">PhonePe Credentials</div>
          <p className="text-xs text-white/50">
            Get these from the{" "}
            <a href="https://business.phonepe.com" target="_blank" rel="noreferrer" className="brand-blue underline">
              PhonePe Business Dashboard → Developer Settings
            </a>. Complete UAT testing before switching to Production.
          </p>
        </div>

        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 mb-1">Environment</div>
          <div className="flex gap-2">
            {[
              { id: "sandbox", label: "Sandbox (UAT)" },
              { id: "production", label: "Production" },
            ].map((m) => (
              <button
                key={m.id}
                data-testid={`pp-env-${m.id}`}
                onClick={() => setForm({ ...form, env: m.id })}
                className={`px-4 py-2 text-xs font-mono uppercase tracking-widest border ${form.env === m.id ? "border-brand-orange brand-orange" : "border-white/20 text-white/60"}`}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>

        <Field label="Merchant ID" hint="Your PhonePe merchant ID (M... prefix in most accounts)">
          <input
            data-testid="pp-merchant-id"
            value={form.merchant_id}
            onChange={(e) => setForm({ ...form, merchant_id: e.target.value })}
            placeholder="M12ABC34DEF"
            className="w-full bg-transparent border border-white/20 py-3 px-4 font-mono text-sm"
          />
        </Field>

        <Field label="Client ID">
          <input
            data-testid="pp-client-id"
            value={form.client_id}
            onChange={(e) => setForm({ ...form, client_id: e.target.value })}
            placeholder="TEST-M12ABC34DEF25012_25012"
            className="w-full bg-transparent border border-white/20 py-3 px-4 font-mono text-sm"
          />
        </Field>

        <Field
          label="Client Secret"
          hint={cfg.client_secret_set ? `Currently: ${cfg.client_secret_masked} — leave blank to keep` : "Long alphanumeric secret from Developer Settings"}
        >
          <input
            data-testid="pp-client-secret"
            type="password"
            value={form.client_secret}
            onChange={(e) => setForm({ ...form, client_secret: e.target.value })}
            placeholder={cfg.client_secret_set ? "•••• (unchanged)" : "Enter secret"}
            className="w-full bg-transparent border border-white/20 py-3 px-4 font-mono text-sm"
          />
        </Field>

        <Field label="Client Version" hint="Integer, typically 1 for UAT">
          <input
            data-testid="pp-client-version"
            type="number"
            min={1}
            value={form.client_version}
            onChange={(e) => setForm({ ...form, client_version: parseInt(e.target.value || "1", 10) })}
            className="w-full bg-transparent border border-white/20 py-3 px-4 font-mono text-sm"
          />
        </Field>

        <div className="border-t border-white/10 pt-6">
          <div className="font-mono text-xs uppercase tracking-widest text-white/50 mb-3">Webhook — SHA auth (recommended)</div>
          <div className="grid gap-4">
            <Field label="Webhook Username">
              <input
                data-testid="pp-webhook-username"
                value={form.webhook_username}
                onChange={(e) => setForm({ ...form, webhook_username: e.target.value })}
                placeholder="htp_webhook"
                className="w-full bg-transparent border border-white/20 py-3 px-4 font-mono text-sm"
              />
            </Field>
            <Field
              label="Webhook Password"
              hint={cfg.webhook_password_set ? `Currently: ${cfg.webhook_password_masked} — leave blank to keep` : "Long random password (server verifies SHA256(username:password))"}
            >
              <input
                data-testid="pp-webhook-password"
                type="password"
                value={form.webhook_password}
                onChange={(e) => setForm({ ...form, webhook_password: e.target.value })}
                placeholder={cfg.webhook_password_set ? "•••• (unchanged)" : "Enter password"}
                className="w-full bg-transparent border border-white/20 py-3 px-4 font-mono text-sm"
              />
            </Field>
          </div>
        </div>

        <details className="border border-white/10 p-4">
          <summary className="cursor-pointer text-xs font-mono uppercase tracking-widest text-white/50">Advanced — HMAC webhook (optional)</summary>
          <div className="grid gap-4 mt-4">
            <Field label="HMAC Key ID">
              <input
                data-testid="pp-hmac-key-id"
                value={form.webhook_hmac_key_id}
                onChange={(e) => setForm({ ...form, webhook_hmac_key_id: e.target.value })}
                className="w-full bg-transparent border border-white/20 py-3 px-4 font-mono text-sm"
              />
            </Field>
            <Field
              label="HMAC Secret"
              hint={cfg.webhook_hmac_secret_set ? "•••• (set) — leave blank to keep" : "Shared secret for HMAC-SHA256 body signing"}
            >
              <input
                data-testid="pp-hmac-secret"
                type="password"
                value={form.webhook_hmac_secret}
                onChange={(e) => setForm({ ...form, webhook_hmac_secret: e.target.value })}
                placeholder={cfg.webhook_hmac_secret_set ? "•••• (unchanged)" : "Enter secret"}
                className="w-full bg-transparent border border-white/20 py-3 px-4 font-mono text-sm"
              />
            </Field>
          </div>
        </details>

        <div className="flex gap-3 pt-2">
          <button
            onClick={save}
            disabled={saving}
            data-testid="pp-save"
            className="bg-brand-blue text-white px-8 py-3 font-bold uppercase tracking-widest text-sm hover:glow-blue disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save"}
          </button>
          <button
            onClick={testConnection}
            disabled={testing || !cfg.is_configured}
            data-testid="pp-test"
            className="border border-white/20 text-white px-6 py-3 font-bold uppercase tracking-widest text-xs hover:bg-white/6 disabled:opacity-50"
          >
            {testing ? "Testing…" : "Test Connection"}
          </button>
        </div>
        {testResult && (
          <div className={`text-xs font-mono p-3 border ${testResult.ok ? "border-green-500/50 text-green-300 bg-green-500/10" : "border-red-500/50 text-red-300 bg-red-500/10"}`} data-testid="pp-test-result">
            {testResult.message}
          </div>
        )}
      </div>

      {/* Per-flow rollout */}
      <div className="max-w-3xl bg-[#131A2B] border border-white/12 p-6" data-testid="pp-flows">
        <div className="font-display text-lg uppercase font-bold brand-orange mb-1">Live Rollout · Per Flow</div>
        <p className="text-xs text-white/60 mb-4">
          Flip each checkout independently. Turn on <b>one flow at a time</b>, test it, then move to the next.
          Turning a flow OFF instantly reverts it back to Razorpay — no code change required.
        </p>
        <div className="grid gap-2">
          {(cfg.all_flows || []).map((kind) => {
            const on = (cfg.enabled_flows || []).includes(kind);
            const label = FLOW_LABELS[kind] || kind;
            return (
              <div key={kind} className="flex items-center justify-between border border-white/10 bg-black/20 px-4 py-3"
                   data-testid={`pp-flow-row-${kind}`}>
                <div>
                  <div className="text-sm font-mono uppercase tracking-widest">{label}</div>
                  <div className="text-[10px] text-white/40">kind = {kind}</div>
                </div>
                <button
                  disabled={!cfg.is_configured}
                  onClick={() => toggleFlow(kind, !on)}
                  data-testid={`pp-flow-toggle-${kind}`}
                  className={`px-4 py-2 font-mono uppercase tracking-widest text-[11px] transition-all ${
                    on
                      ? "bg-green-500/20 border border-green-400/50 text-green-300"
                      : "bg-white/5 border border-white/15 text-white/60 hover:bg-white/10"
                  } ${!cfg.is_configured ? "opacity-40 cursor-not-allowed" : ""}`}
                >
                  {on ? "PhonePe · Live" : "Razorpay"}
                </button>
              </div>
            );
          })}
        </div>
        {!cfg.is_configured && (
          <div className="text-[10px] text-white/40 mt-3">
            Save credentials + run Test Connection first — then flip a flow to go live.
          </div>
        )}
      </div>

      {/* Webhook URL */}
      <div className="max-w-3xl bg-[#131A2B] border border-white/12 p-6 space-y-3">
        <div className="font-display text-lg uppercase font-bold brand-orange">Webhook URL</div>
        <p className="text-xs text-white/60">
          In PhonePe Business Dashboard → Developer Settings → Webhooks, add this URL and select
          <b> SHA</b> authentication. Enable events:{" "}
          <span className="brand-blue">checkout.order.completed</span>,{" "}
          <span className="brand-blue">checkout.order.failed</span>,{" "}
          <span className="brand-blue">pg.refund.completed</span>,{" "}
          <span className="brand-blue">pg.refund.failed</span>.
        </p>
        <div className="flex items-center gap-2 bg-black/40 border border-white/12 px-3 py-2">
          <code className="flex-1 font-mono text-xs text-white/80 truncate" data-testid="pp-webhook-url">{webhookUrl}</code>
          <button onClick={() => copy(webhookUrl)} className="text-white/60 hover:text-white" data-testid="pp-copy-webhook"><Copy size={14} /></button>
        </div>
      </div>
    </div>
  );
}

// ---------------- Razorpay (existing, unchanged) ----------------
function RazorpayPanel() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({
    razorpay_key_id: "",
    razorpay_key_secret: "",
    razorpay_webhook_secret: "",
    mode: "test",
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [webhookUrl, setWebhookUrl] = useState("");

  useEffect(() => {
    load();
    setWebhookUrl(`${window.location.origin}/api/webhook/razorpay`);
  }, []);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/payment-config");
      setCfg(data);
      setForm({
        razorpay_key_id: data.razorpay_key_id || "",
        razorpay_key_secret: "",
        razorpay_webhook_secret: "",
        mode: data.mode || "test",
      });
    } catch (e) { toast.error(apiError(e)); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { mode: form.mode, razorpay_key_id: form.razorpay_key_id };
      if (form.razorpay_key_secret.trim()) payload.razorpay_key_secret = form.razorpay_key_secret.trim();
      if (form.razorpay_webhook_secret.trim()) payload.razorpay_webhook_secret = form.razorpay_webhook_secret.trim();
      const { data } = await api.post("/admin/payment-config", payload);
      setCfg(data);
      setForm({ ...form, razorpay_key_secret: "", razorpay_webhook_secret: "" });
      toast.success("Configuration saved");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const testConnection = async () => {
    setTesting(true); setTestResult(null);
    try {
      const { data } = await api.post("/admin/payment-config/test-connection");
      setTestResult(data);
      if (data.ok) toast.success(data.message);
      else toast.error(data.message);
    } catch (e) { toast.error(apiError(e)); }
    finally { setTesting(false); }
  };

  const goLive = async () => {
    if (!cfg?.is_live) {
      if (!confirm("Go LIVE with Razorpay payments? Bookings, invoices and tournament fees will start accepting real money.")) return;
    }
    try {
      const { data } = await api.post("/admin/payment-config/go-live", { is_live: !cfg?.is_live });
      setCfg(data);
      toast.success(data.is_live ? "Payments are LIVE" : "Payments turned OFF");
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggleDonations = async () => {
    try {
      const { data } = await api.post("/admin/payment-config/donations-toggle", { donations_enabled: !cfg?.donations_enabled });
      setCfg(data);
      toast.success(data.donations_enabled ? "Donations page is now LIVE" : "Donations disabled");
    } catch (e) { toast.error(apiError(e)); }
  };

  const copy = (v) => { navigator.clipboard.writeText(v); toast.success("Copied"); };

  if (!cfg) return <div className="text-white/60">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="razorpay-panel">
      <div className={`border p-6 ${cfg.is_live ? "border-green-500/50 bg-green-500/10" : "border-white/12 bg-[#131A2B]"}`}>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className={`h-3 w-3 rounded-full ${cfg.is_live ? "bg-green-400 animate-pulse" : "bg-white/30"}`} />
            <div>
              <div className="font-display text-2xl uppercase font-black leading-none">
                {cfg.is_live ? "PAYMENTS LIVE" : "PAYMENTS OFF"}
              </div>
              <div className="font-mono text-xs uppercase tracking-widest text-white/50 mt-1">
                Mode · <span className={cfg.mode === "live" ? "text-brand-orange" : "brand-blue"}>{cfg.mode.toUpperCase()}</span>
              </div>
            </div>
          </div>
          <button
            onClick={goLive}
            data-testid="pc-go-live"
            disabled={!cfg.has_key_secret || !cfg.razorpay_key_id}
            className={`px-8 py-4 font-bold uppercase tracking-widest text-sm transition-all ${
              cfg.is_live
                ? "bg-white/10 text-white hover:bg-white/20"
                : "bg-brand-orange text-black hover:glow-orange disabled:opacity-40 disabled:cursor-not-allowed"
            }`}
          >
            {cfg.is_live ? "Turn OFF Payments" : "GO LIVE"}
          </button>
        </div>
      </div>

      <div className="max-w-3xl bg-[#131A2B] border border-white/12 p-8 space-y-6" data-testid="pc-form">
        <div>
          <div className="font-display text-lg uppercase font-bold brand-orange mb-1">Razorpay Credentials</div>
          <p className="text-xs text-white/50">
            Get these from{" "}
            <a href="https://dashboard.razorpay.com/app/keys" target="_blank" rel="noreferrer" className="brand-blue underline">
              Razorpay Dashboard → Settings → API Keys
            </a>.
          </p>
        </div>

        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 mb-1">Mode</div>
          <div className="flex gap-2">
            {["test", "live"].map((m) => (
              <button
                key={m}
                data-testid={`pc-mode-${m}`}
                onClick={() => setForm({ ...form, mode: m })}
                className={`px-4 py-2 text-xs font-mono uppercase tracking-widest border ${form.mode === m ? "border-brand-orange brand-orange" : "border-white/20 text-white/60"}`}
              >
                {m}
              </button>
            ))}
          </div>
        </div>

        <Field label="Key ID" hint="Starts with rzp_test_ or rzp_live_">
          <input
            value={form.razorpay_key_id}
            onChange={(e) => setForm({ ...form, razorpay_key_id: e.target.value })}
            data-testid="pc-key-id"
            placeholder="rzp_test_xxxxxxxxxxxx"
            className="w-full bg-transparent border border-white/20 py-3 px-4 font-mono text-sm"
          />
        </Field>

        <Field label="Key Secret" hint={cfg.has_key_secret ? `Currently: ${cfg.razorpay_key_secret_masked} — leave blank to keep` : "Long alphanumeric secret"}>
          <input
            type="password"
            value={form.razorpay_key_secret}
            onChange={(e) => setForm({ ...form, razorpay_key_secret: e.target.value })}
            data-testid="pc-key-secret"
            placeholder={cfg.has_key_secret ? "•••• (unchanged)" : "Enter secret"}
            className="w-full bg-transparent border border-white/20 py-3 px-4 font-mono text-sm"
          />
        </Field>

        <Field label="Webhook Secret" hint={cfg.has_webhook_secret ? `Currently: ${cfg.razorpay_webhook_secret_masked}` : "Set this in Razorpay → Webhooks"}>
          <input
            type="password"
            value={form.razorpay_webhook_secret}
            onChange={(e) => setForm({ ...form, razorpay_webhook_secret: e.target.value })}
            data-testid="pc-webhook-secret"
            placeholder={cfg.has_webhook_secret ? "•••• (unchanged)" : "Enter secret"}
            className="w-full bg-transparent border border-white/20 py-3 px-4 font-mono text-sm"
          />
        </Field>

        <div className="flex gap-3 pt-2">
          <button onClick={save} disabled={saving} data-testid="pc-save"
            className="bg-brand-blue text-white px-8 py-3 font-bold uppercase tracking-widest text-sm hover:glow-blue disabled:opacity-50">
            {saving ? "Saving…" : "Save"}
          </button>
          <button onClick={testConnection} disabled={testing || !cfg.has_key_secret} data-testid="pc-test"
            className="border border-white/20 text-white px-6 py-3 font-bold uppercase tracking-widest text-xs hover:bg-white/6 disabled:opacity-50">
            {testing ? "Testing…" : "Test Connection"}
          </button>
        </div>
        {testResult && (
          <div className={`text-xs font-mono p-3 border ${testResult.ok ? "border-green-500/50 text-green-300 bg-green-500/10" : "border-red-500/50 text-red-300 bg-red-500/10"}`}>
            {testResult.message}
          </div>
        )}
      </div>

      <div className="max-w-3xl bg-[#131A2B] border border-white/12 p-6 space-y-3">
        <div className="font-display text-lg uppercase font-bold brand-orange">Webhook Setup</div>
        <div className="flex items-center gap-2 bg-black/40 border border-white/12 px-3 py-2">
          <code className="flex-1 font-mono text-xs text-white/80 truncate" data-testid="pc-webhook-url">{webhookUrl}</code>
          <button onClick={() => copy(webhookUrl)} className="text-white/60 hover:text-white"><Copy size={14} /></button>
        </div>
      </div>

      <div className={`max-w-3xl border p-6 ${cfg.donations_enabled ? "border-brand-orange/50 bg-brand-orange/10" : "border-white/12 bg-[#131A2B]"}`} data-testid="pc-donations">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <HeartHandshake className="brand-orange" size={28} />
            <div>
              <div className="font-display text-xl uppercase font-black leading-none">80G Donations</div>
              <div className="text-xs text-white/60 mt-1">
                Public <a href="/donate" className="brand-blue underline">/donate</a> page + &quot;Support the Academy&quot; CTAs go live.
              </div>
            </div>
          </div>
          <button
            onClick={toggleDonations}
            data-testid="pc-donations-toggle"
            disabled={!cfg.is_live}
            className={`px-6 py-3 font-bold uppercase tracking-widest text-xs transition-all ${
              cfg.donations_enabled
                ? "bg-white/10 text-white hover:bg-white/20"
                : "bg-brand-orange text-black hover:glow-orange disabled:opacity-40 disabled:cursor-not-allowed"
            }`}
          >
            {cfg.donations_enabled ? "Disable Donations" : "Enable Donations"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <div className="font-mono text-xs uppercase tracking-widest text-white/40 mb-1">{label}</div>
      {children}
      {hint && <div className="text-[10px] text-white/40 mt-1">{hint}</div>}
    </div>
  );
}
